import {
  makeObservable,
  action,
  computed,
  observable,
  runInAction,
} from 'mobx';
import { ROUTES } from '../routes';
import { RootStore } from './root';
import { AccountData, AccountDataExtended } from '@wix/identity-fed-common';
import {
  loginAccountSelectorAccountSelected,
  loginAccountSelectorComponentLoad,
} from '@wix/bi-logger-hls2/v2';
import {
  AUTH_METHODS_BY_MAIL,
  SEARCH_PARAMS,
  WIX_DEFAULT_ORGANIZATION_ID,
  WIX_HOME_URL,
  WIX_PARENT_ACCOUNT_HINT,
} from '../utils/constants';
import { shouldRouteBackToLoginInApp } from './constants';
import {
  AccountData as UserAccountData,
  IdpOrganizationSelection,
  SelectableOrganization,
  UserAccountsDataByEmailResponse,
} from '../types';
import { HttpError } from '@wix/http-client';
import { StateType } from '@wix/ambassador-iam-authentication-v1-authentication/types';
import { CAPTCHA_ACTIONS } from './captcha';
import { organizationIdOf } from '../utils/requestedOrganization';
import { AuthMethod, setLastUsedAuthMethod } from '../utils/lastUsedAuthMethod';
import { normalizeEmail } from '../utils/general';

export enum AccountsFilter {
  SSO = 'SSO',
}

type SelectorAccount = UserAccountData & AccountData & { isBlocked?: boolean };

/**
 * An offered organization as the selector shows it, from IAM's offer alone.
 * Without a name the selector shows its default one.
 */
const toSelectorAccount = (
  offered: SelectableOrganization,
): SelectorAccount => ({
  accountId: offered.userId,
  parentAccountId:
    offered.organizationId === WIX_DEFAULT_ORGANIZATION_ID
      ? undefined
      : offered.organizationId,
  accountDisplayData: {
    ...(offered.displayName && { name: offered.displayName }),
    ...(offered.logoUrl && { imageUrl: offered.logoUrl }),
  },
  isBlocked: offered.blocked,
});

export class SelectAccountStore {
  private readonly rootStore: RootStore;
  public showError: boolean = false;
  public filter: string = '';
  private captchaAdded: boolean = false;
  public userAccountsDataByEmail?: UserAccountsDataByEmailResponse;
  /** An IdP login waiting for one of its email's organizations to be picked here. */
  private idpSelection?: IdpOrganizationSelection;
  private idpSelectionListenerId?: string;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      showError: observable,
      filter: observable,
      userAccountsDataByEmail: observable,
      email: computed,
      showAccountSelector: computed,
      initialFetch: action,
      onErrorOccured: action,
      resetError: action,
      onAccountSelected: action,
      fetchSsoAccounts: action.bound,
      fetchAccounts: action.bound,
      onBackClicked: action.bound,
      offerIdpOrganizations: action.bound,
    });
    this.setAndHandleFilter();
  }

  get email() {
    return this.rootStore.userDataStore.email;
  }

  public onErrorOccured = () => {
    this.showError = true;
  };

  public resetError = () => {
    this.showError = false;
  };

  public onAccountSelectorLoaded = (accountsData: AccountData[]) => {
    this.rootStore.biLogger.report(
      loginAccountSelectorComponentLoad({
        account_ids: accountsData
          .map((account) => account.accountId)
          .toString(),
        parent_account_ids: accountsData
          .map((account) => account.parentAccountId)
          .filter((id) => id)
          .toString(),
        context: 'login',
      }),
    );
  };

  /** The next visit to the selector offers these organizations, not the lookup's accounts. */
  public offerIdpOrganizations(selection: IdpOrganizationSelection) {
    const { navigationStore } = this.rootStore;
    this.dropIdpSelection();
    this.idpSelection = selection;
    this.userAccountsDataByEmail = undefined;
    // Leaving the selector any way (browser back included) abandons the
    // offer, so a later visit doesn't continue this login.
    this.idpSelectionListenerId = navigationStore.registerHistoryChanges(
      (location) => {
        if (location.pathname !== ROUTES.SELECT_ACCOUNT) {
          this.dropIdpSelection();
        }
      },
    );
  }

  private dropIdpSelection() {
    if (this.idpSelection) {
      // Its rows aren't the lookup's: a later visit looks the email up.
      runInAction(() => {
        this.userAccountsDataByEmail = undefined;
      });
    }
    this.idpSelection = undefined;
    if (this.idpSelectionListenerId) {
      this.rootStore.navigationStore.unRegisterHistoryChanges(
        this.idpSelectionListenerId,
      );
      this.idpSelectionListenerId = undefined;
    }
  }

  /** The IdP login waiting for a pick, unless it was for another email. */
  private idpSelectionFor(email: string) {
    const selection = this.idpSelection;
    return selection &&
      normalizeEmail(selection.email) === normalizeEmail(email)
      ? selection
      : undefined;
  }

  // The selector passes back the looked-up account, authenticators included.
  public onAccountSelected = async (
    account: AccountDataExtended &
      Pick<UserAccountData, 'userAuthenticatorsData'>,
  ) => {
    // If there is a parentAccountId, try to get an SSO url
    this.rootStore.biLogger.report(
      loginAccountSelectorAccountSelected({
        parent_account_id: account.parentAccountId,
        selected_account_id: account.accountId,
      }),
    );
    const idpSelection = this.idpSelectionFor(this.email);
    if (idpSelection) {
      return this.continueIdpLogin(idpSelection, account);
    }
    this.rootStore.userDataStore.setResolvedAccount(this.email, account, {
      pickedInSelector: true,
    });
    const { navigationStore } = this.rootStore;
    const { targetUrlForSso } = navigationStore;
    // The selected account is currently logged in
    if (account.loggedInUserData) {
      targetUrlForSso
        ? navigationStore.redirect(targetUrlForSso)
        : navigationStore.redirect(WIX_HOME_URL);
    } else {
      const ssoUrl = account.accountSsoSettings?.ssoUrl;
      if (ssoUrl) {
        // Same as the approve-and-continue path: written before we hand the
        // browser to the IDP.
        setLastUsedAuthMethod(AuthMethod.SSO);
        const { locationParamsEntries } = navigationStore;
        this.rootStore.navigationStore.appendSearchParamToCurrentLocation(
          SEARCH_PARAMS.EMAIL,
          this.email,
        );
        const searchParams = {
          ...locationParamsEntries,
        };
        targetUrlForSso &&
          (searchParams[SEARCH_PARAMS.TARGET_URL] = targetUrlForSso);
        this.rootStore.navigationStore.redirect(ssoUrl, {
          searchParams,
        });
      } else if (shouldRouteBackToLoginInApp(this.rootStore.experiments)) {
        return this.continueLoginInApp(account);
      } else {
        if (this.rootStore.isNewLoginApp) {
          if (await this.tryPasskeyLogin(account)) {
            return;
          }
          return this.rootStore.loginStore.onNavigateToLogin();
        }
        navigationStore.goToMainLogin({
          email: this.email,
          parentHint: account.parentAccountId || WIX_PARENT_ACCOUNT_HINT,
        });
      }
    }
  };

  private async continueIdpLogin(
    selection: IdpOrganizationSelection,
    account: Pick<UserAccountData, 'parentAccountId'>,
  ) {
    // Its state token is single-use: coming back here looks the email up
    // again, as for any login waiting for its first factor.
    this.dropIdpSelection();
    const { emailStepStore, idpConnectionLoginStore } = this.rootStore;
    this.rootStore.setIsLoading(true);
    try {
      await idpConnectionLoginStore.continueWithOrganization(
        selection,
        organizationIdOf(account),
      );
    } finally {
      // The first factor's email lookup keeps the page loading until it
      // routes away.
      if (!emailStepStore.isLoading) {
        this.rootStore.setIsLoading(false);
      }
    }
  }

  /**
   * Continues the login with the picked account without leaving the page, so
   * a login in progress keeps its state: a redirect to the login page drops
   * an IdP login waiting for its first factor, and the merge never finishes.
   */
  private async continueLoginInApp(
    account: Pick<
      UserAccountData,
      'userAuthenticatorsData' | 'parentAccountId'
    >,
  ) {
    const {
      socialAuthStore,
      emailStepStore,
      ssoStore,
      loginStore,
      idpConnectionLoginStore,
    } = this.rootStore;
    // No password to log in with: its social providers, as the email step
    // routes such an account.
    if (socialAuthStore.shouldProceedToSocialAuthOnly(account)) {
      this.rootStore.setIsLoading(false);
      // The picked account has no SSO link; drop one an earlier lookup left.
      ssoStore.setSsoPageUrl('');
      return emailStepStore.authHandlers[AUTH_METHODS_BY_MAIL.SOCIAL_ONLY]({
        socialConnectionsProviders:
          account.userAuthenticatorsData?.socialConnectionsProviders ?? [],
      });
    }
    if (await this.tryPasskeyLogin(account)) {
      return;
    }
    loginStore.onNavigateToLogin(undefined, { isEmailReadOnly: true });
    // Routing to the selector cleared the form's IdP flag, but an IdP login
    // waiting for its first factor still continues on the password form.
    if (
      loginStore.loginFlow.tokenAt(this.email, StateType.REQUIRE_FIRST_FACTOR)
    ) {
      idpConnectionLoginStore.isIdpFirstFactorFlow = true;
    }
  }

  /**
   * Tries the picked account's passkey before its password form, for an
   * account with a password, as the email step does. The challenge names the
   * picked account's organization. The page stays loading meanwhile, so
   * another pick can't start while the prompt is open.
   */
  private async tryPasskeyLogin(
    account: Pick<UserAccountData, 'userAuthenticatorsData'>,
  ) {
    if (!account.userAuthenticatorsData?.hasPassword) {
      this.rootStore.setIsLoading(false);
      return false;
    }
    this.rootStore.setIsLoading(true);
    try {
      return await this.rootStore.webauthnStore.tryPasskeyLogin(this.email);
    } finally {
      this.rootStore.setIsLoading(false);
    }
  }

  public async fetchSsoAccounts() {
    const res = await this.fetchAccounts();
    const userAccountsDataByEmail = res;
    if (!userAccountsDataByEmail?.accountsData) {
      return;
    }
    userAccountsDataByEmail.accountsData =
      this.rootStore.ssoStore.filterSsoAccounts(
        userAccountsDataByEmail.accountsData,
      );
    return userAccountsDataByEmail;
  }

  public async fetchAccounts() {
    const { userDataStore, apiStore } = this.rootStore;
    try {
      const recaptchaParams =
        await this.rootStore.captchaStore.handleRecaptchaExecution({
          captchaAdded: this.captchaAdded,
          action: CAPTCHA_ACTIONS.GET_USER_ACCOUNTS,
        });
      const res = await apiStore.getUserAccounts({
        email: userDataStore.email,
        ...recaptchaParams,
      });
      return res;
    } catch (error) {
      const serverErrorCode = (
        error as HttpError
      )?.response?.data.errorCode?.toString();
      this.captchaAdded =
        this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
          serverErrorCode,
          this.captchaAdded,
        );

      console.error(error);
      this.onErrorOccured();
    }
  }

  get showAccountSelector() {
    return (
      this.showError ||
      (this.userAccountsDataByEmail?.accountsData?.length ?? 0) > 1
    );
  }

  public setAndHandleFilter(filter?: AccountsFilter) {
    this.filter =
      filter ??
      this.rootStore.navigationStore.getQueryParam('filterAccounts') ??
      '';
  }

  public async initialFetch() {
    this.resetError();
    const idpSelection = this.idpSelectionFor(this.email);
    if (idpSelection) {
      // IAM's offer, in its order: the email isn't looked up again.
      this.userAccountsDataByEmail = {
        accountsData: idpSelection.organizations.map(toSelectorAccount),
      };
      return;
    }
    let fetchUserAccounts = this.fetchAccounts;
    if (this.filter === AccountsFilter.SSO) {
      fetchUserAccounts = this.fetchSsoAccounts;
    }
    this.userAccountsDataByEmail = await fetchUserAccounts();
    const { accountsData } = this.userAccountsDataByEmail ?? {
      accountsData: [],
    };
    if (!accountsData?.length || accountsData.length > 1) {
      return;
    }
    this.onAccountSelected({
      ...accountsData[0],
      loggedInUserData: this.userAccountsDataByEmail?.loggedInUserData,
    });
  }

  public enableAccess() {
    return !!this.email;
  }
  public onBackClicked() {
    this.userAccountsDataByEmail = undefined;
    this.dropIdpSelection();
    this.setAndHandleFilter();
    this.rootStore.navigationStore.goBack();
  }

  get isSelectAccountVisible() {
    return (
      this.rootStore.navigationStore.currentRoute === ROUTES.SELECT_ACCOUNT
    );
  }
}
